import { memo, useCallback, useState } from "react";
import { ReactSVG } from "react-svg";

// Helpers
import { classNames } from "helpers";

import { DamagePhotoGalleryProps } from "./types";

/**
 * Galerija fotografija štete u tooltipu. Učitava se lijeno: sadržaj tooltipa nastaje
 * tek na hover, a od tada se dohvaća samo prikazana slika — susjedne čekaju svoj red.
 */
const DamagePhotoGallery: React.FC<DamagePhotoGalleryProps> = (props) => {
  const { photos } = props;

  const [index, setIndex] = useState<number>(0);
  // Jednom prikazana slika ostaje u DOM-u, pa listanje naprijed-nazad ne učitava iznova.
  const [loadedIndexes, setLoadedIndexes] = useState<number[]>([0]);

  const showPhoto = useCallback((next: number) => {
    setIndex(next);
    setLoadedIndexes((prev) => (prev.includes(next) ? prev : [...prev, next]));
  }, []);

  const handlePrevClick = useCallback(() => {
    showPhoto((index - 1 + photos.length) % photos.length);
  }, [index, photos.length, showPhoto]);

  const handleNextClick = useCallback(() => {
    showPhoto((index + 1) % photos.length);
  }, [index, photos.length, showPhoto]);

  // Tooltip je u portalu, ali React događaji putuju kroz stablo komponenti — bez ovoga
  // bi klik u galeriji dopro do retka tablice i otvorio uređivanje štete.
  const handleGalleryClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  if (photos.length === 0) return <></>;

  const current = photos[index];
  const hasMultiple = photos.length > 1;

  return (
    <div className="w-64" onClick={handleGalleryClick}>
      <div className="relative rounded overflow-hidden bg-slate-900">
        {photos.map((photo, photoIndex) =>
          loadedIndexes.includes(photoIndex) ? (
            <img
              key={photo.id}
              src={photo.url}
              alt=""
              loading="lazy"
              decoding="async"
              className={classNames(
                "w-full h-40 object-cover",
                photoIndex === index ? "block" : "hidden"
              )}
            />
          ) : null
        )}

        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={handlePrevClick}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center"
            >
              <ReactSVG
                src="/media/icons/arrow-left.svg"
                className="[&_svg]:w-4 [&_svg]:h-4 [&_path]:stroke-white"
              />
            </button>
            <button
              type="button"
              onClick={handleNextClick}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center"
            >
              <ReactSVG
                src="/media/icons/arrow-right.svg"
                className="[&_svg]:w-4 [&_svg]:h-4 [&_path]:stroke-white"
              />
            </button>
            <span className="absolute bottom-1 right-2 text-xs text-white bg-black/50 rounded-full px-2 py-0.5">
              {index + 1} / {photos.length}
            </span>
          </>
        )}
      </div>

      <div className="mt-2 text-xs text-slate-300 truncate">
        {current.originalFileName}
      </div>
    </div>
  );
};

export default memo(DamagePhotoGallery);
